import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Zap, CheckCircle, XCircle, Activity, AlertTriangle,
  Clock, Users, Globe, TrendingUp, BarChart2, Wifi, RefreshCw,
  ExternalLink,
} from 'lucide-react';
import api from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface K6Execution {
  id: string;
  project_id: string | null;
  capture_id: string;
  skope_scan_id: string | null;
  network_profile: string;
  vus: number;
  duration_seconds: number;
  target_url: string | null;
  status: 'running' | 'passed' | 'failed' | 'error';
  error_message: string | null;
  thresholds_passed: boolean;
  p50_ms: number | null;
  p90_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
  avg_ms: number | null;
  max_ms: number | null;
  rps: number | null;
  total_requests: number | null;
  total_iterations: number | null;
  error_rate: number | null;
  error_count: number | null;
  timeout_count: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PROFILE_LABELS: Record<string, string> = {
  desktop: 'Desktop / WiFi',
  '4g': '4G LTE',
  '3g': '3G HSPA+',
  '2g': '2G EDGE',
};

const STATUS_CONFIG = {
  passed:  { label: 'Passed',  icon: CheckCircle,  bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  failed:  { label: 'Failed',  icon: XCircle,      bg: 'bg-red-500/10',     text: 'text-red-400',     border: 'border-red-500/30' },
  running: { label: 'Running', icon: Activity,     bg: 'bg-blue-500/10',    text: 'text-blue-400',    border: 'border-blue-500/30' },
  error:   { label: 'Error',   icon: AlertTriangle, bg: 'bg-yellow-500/10', text: 'text-yellow-400',  border: 'border-yellow-500/30' },
};

function fmtMs(ms: number | null): string {
  if (ms == null) return '—';
  return `${ms.toFixed(0)} ms`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' });
}

function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function p95Color(ms: number | null): string {
  if (ms == null) return 'text-slate-400';
  if (ms < 500)  return 'text-emerald-400';
  if (ms < 2000) return 'text-yellow-400';
  return 'text-red-400';
}

// ── Metric Card ───────────────────────────────────────────────────────────────

function MetricCard({
  label, value, sub, accent = false, warn = false,
}: {
  label: string; value: string; sub?: string; accent?: boolean; warn?: boolean;
}) {
  const valClass = warn ? 'text-red-400' : accent ? 'text-violet-400' : 'text-white';
  return (
    <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">{label}</p>
      <p className={`text-2xl font-bold font-mono ${valClass}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

// ── Percentile Bar ────────────────────────────────────────────────────────────

function PercentileRow({ label, ms, threshold }: { label: string; ms: number | null; threshold?: number }) {
  const pct  = ms && threshold ? Math.min((ms / threshold) * 100, 100) : null;
  const over = threshold && ms ? ms > threshold : false;
  return (
    <div className="flex items-center gap-4">
      <span className="text-xs text-slate-400 w-8 shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
        {pct != null && (
          <div
            className={`h-full rounded-full transition-all ${over ? 'bg-red-500' : 'bg-violet-500'}`}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      <span className={`text-sm font-mono w-20 text-right ${p95Color(ms)}`}>
        {fmtMs(ms)}
      </span>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ExecutionDetail() {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNavigate();
  const [exec, setExec]     = useState<K6Execution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!executionId) return;
    (async () => {
      try {
        const data = await api.getK6Execution(executionId);
        setExec(data);
      } catch (e: any) {
        setError(e?.response?.data?.detail ?? 'No se pudo cargar la ejecución');
      } finally {
        setLoading(false);
      }
    })();
  }, [executionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-dark flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Cargando ejecución...</span>
        </div>
      </div>
    );
  }

  if (error || !exec) {
    return (
      <div className="min-h-screen bg-background-dark p-6">
        <button onClick={() => navigate('/performance')} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <p>{error ?? 'Ejecución no encontrada'}</p>
        </div>
      </div>
    );
  }

  const st = STATUS_CONFIG[exec.status] ?? STATUS_CONFIG.error;
  const StatusIcon = st.icon;
  const errPct = exec.error_rate != null ? (exec.error_rate * 100) : null;

  // Elapsed time
  let elapsed = '—';
  if (exec.started_at && exec.finished_at) {
    const ms = new Date(exec.finished_at).getTime() - new Date(exec.started_at).getTime();
    elapsed = fmtDuration(Math.round(ms / 1000));
  }

  return (
    <div className="min-h-screen bg-background-dark text-white p-6">

      {/* ── Back ── */}
      <button
        onClick={() => navigate('/performance')}
        className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Performance Dashboard
      </button>

      {/* ── Header card ── */}
      <div className={`border rounded-xl p-5 mb-6 ${st.bg} ${st.border}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-black/20 rounded-lg">
              <Zap className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full font-medium ${st.bg} ${st.text} border ${st.border}`}>
                  <StatusIcon className="w-3.5 h-3.5" />
                  {st.label}
                </span>
                {exec.thresholds_passed && (
                  <span className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full">
                    ✓ Thresholds OK
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Globe className="w-4 h-4 text-slate-500" />
                <a
                  href={exec.target_url ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium hover:text-violet-400 transition-colors flex items-center gap-1"
                >
                  {exec.target_url ?? '—'}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6 text-sm text-slate-400 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Wifi className="w-4 h-4" />
              {PROFILE_LABELS[exec.network_profile] ?? exec.network_profile}
            </div>
            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              {exec.vus} VUs
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {fmtDuration(exec.duration_seconds)}
            </div>
          </div>
        </div>

        {exec.error_message && (
          <div className="mt-3 p-3 bg-black/20 rounded-lg text-sm text-red-300 font-mono">
            {exec.error_message}
          </div>
        )}
      </div>

      {/* ── KPI Grid ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Total Requests"
          value={exec.total_requests?.toLocaleString() ?? '—'}
          sub={exec.rps != null ? `${exec.rps.toFixed(1)} req/s` : undefined}
          accent
        />
        <MetricCard
          label="Iteraciones"
          value={exec.total_iterations?.toLocaleString() ?? '—'}
        />
        <MetricCard
          label="Error Rate"
          value={errPct != null ? `${errPct.toFixed(2)}%` : '—'}
          sub={exec.error_count != null ? `${exec.error_count} errores` : undefined}
          warn={errPct != null && errPct > 1}
        />
        <MetricCard
          label="Tiempo real"
          value={elapsed}
          sub={exec.started_at ? fmtDate(exec.started_at) : undefined}
        />
      </div>

      {/* ── Percentiles + Stats ── */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">

        {/* Percentiles */}
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 className="w-4 h-4 text-violet-400" />
            <h2 className="text-sm font-semibold text-white">Percentiles de respuesta</h2>
          </div>
          <div className="space-y-3">
            <PercentileRow label="P50"  ms={exec.p50_ms} threshold={500} />
            <PercentileRow label="P90"  ms={exec.p90_ms} threshold={1000} />
            <PercentileRow label="P95"  ms={exec.p95_ms} threshold={2000} />
            <PercentileRow label="P99"  ms={exec.p99_ms} threshold={5000} />
            <div className="pt-2 border-t border-slate-800/60 space-y-3">
              <PercentileRow label="Avg" ms={exec.avg_ms} />
              <PercentileRow label="Max" ms={exec.max_ms} />
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-violet-400" />
            <h2 className="text-sm font-semibold text-white">Resumen de la ejecución</h2>
          </div>
          <div className="space-y-3">
            {[
              { label: 'P95', value: fmtMs(exec.p95_ms), highlight: true },
              { label: 'Throughput', value: exec.rps != null ? `${exec.rps.toFixed(2)} req/s` : '—' },
              { label: 'Total requests', value: exec.total_requests?.toLocaleString() ?? '—' },
              { label: 'Timeouts', value: exec.timeout_count?.toString() ?? '0' },
              { label: 'Perfil de red', value: PROFILE_LABELS[exec.network_profile] ?? exec.network_profile },
              { label: 'VUs configurados', value: `${exec.vus}` },
              { label: 'Duración config.', value: fmtDuration(exec.duration_seconds) },
              { label: 'Inicio', value: fmtDate(exec.started_at) },
              { label: 'Fin', value: fmtDate(exec.finished_at) },
            ].map(({ label, value, highlight }) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-sm text-slate-400">{label}</span>
                <span className={`text-sm font-mono ${highlight ? p95Color(exec.p95_ms) + ' font-bold' : 'text-white'}`}>
                  {value}
                </span>
              </div>
            ))}
          </div>

          {/* Link to scan */}
          {exec.skope_scan_id && (
            <div className="mt-4 pt-4 border-t border-slate-800/60">
              <Link
                to={`/scans/${exec.skope_scan_id}`}
                className="flex items-center gap-2 text-sm text-violet-400 hover:text-violet-300 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Ver scan de origen
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ── Execution ID ── */}
      <p className="text-xs text-slate-600 font-mono">
        Execution ID: {exec.id}
      </p>
    </div>
  );
}
